import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks, articleMeta, absoluteUrl } from "@/lib/seo";
import { articleHeroFor } from "@/lib/article-heroes";
import { BlogPost, articleJsonLd, breadcrumbJsonLd } from "@/components/BlogPost";
import { MiksiToimii } from "@/components/hub/Callouts";
import hero from "@/assets/hub/nordic-montessori-2026.jpg";
import konferenssi from "@/assets/hub/nordic-montessori-2026-konferenssi.jpg";
import liike from "@/assets/hub/nordic-montessori-2026-liike.jpg";

const SLUG = "nordic-montessori-konferenssi-2026";
const URL = `https://montessori24.fi/montessori-koulu/${SLUG}`;
const TITLE = "Montessori palaa juurilleen — Pohjoismaiden konferenssi Helsingørissä 2026";
const DESC =
  "9. Nordic Montessori Conference kokoontuu 28.–30.8.2026 Helsingøriin, samaan paikkaan, jossa AMI sai alkunsa vuonna 1929. Katso ohjelma, puhujat ja miksi tapahtuma kiinnostaa myös vanhempia.";
const PUBLISHED = "2026-08-23";
const HERO = articleHeroFor(SLUG);

export const Route = createFileRoute("/montessori-koulu/nordic-montessori-konferenssi-2026")({
  head: () => ({
    meta: articleMeta({
      slug: SLUG,
      title: TITLE,
      description: DESC,
      published: PUBLISHED,
      image: HERO,
    }),
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks(URL)],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          articleJsonLd({ title: TITLE, description: DESC, url: URL, image: absoluteUrl(HERO), publishedISO: PUBLISHED }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd(SLUG, TITLE)) },
    ],
  }),
  component: Post,
});

function Post() {
  return (
    <BlogPost
      slug={SLUG}
      eyebrow="Ajankohtaista"
      title="Montessori palaa juurilleen — Pohjoismaiden suuri Montessori-konferenssi kokoontuu Helsingørissä elokuussa 2026"
      lead="28.–30. elokuuta 2026 Montessori-opettajia, koulunjohtajia ja asiantuntijoita eri maista kokoontuu Tanskan Helsingøriin yhdeksänteen Nordic Montessori Conference -tapahtumaan. Paikka ei ole sattumaa."
      readingMinutes={10}
      publishedISO={PUBLISHED}
      updatedISO={PUBLISHED}
      heroImage={hero}
      heroAlt="Historiallinen rantahotelli Helsingørissä loppukesän valossa"
    >
      <p>
        Lähes sata vuotta aikaisemmin samassa kaupungissa Maria Montessori ja hänen poikansa Mario
        olivat mukana tapahtumissa, joiden yhteydessä syntyi Association Montessori Internationale
        eli AMI. Vuonna 2026 Montessori-liike siis tavallaan palaa takaisin paikkaan, jossa yksi
        sen kansainvälisen historian tärkeimmistä vaiheista alkoi.
      </p>
      <p>
        Konferenssin nimi tiivistää ajatuksen hyvin: <em>“Where It All Began – A Pre-Celebration.”</em>
      </p>
      <p>
        Tapahtuma järjestetään 28.–30.8.2026 Marienlyst Strandhotelissa Helsingørissä, ja ohjelman
        tavoitteena on tarkastella Montessori-ajattelua paljon laajemmin kuin vain päiväkodin tai
        alakoulun näkökulmasta — aina syntymästä vanhuuteen asti.
      </p>

      <h2>Helsingør 1929 — paikka, jossa Montessori muuttui kansainväliseksi liikkeeksi</h2>
      <p>
        Kesällä 1929 Helsingørissä järjestettiin suuri New Education Fellowshipin kansainvälinen
        koulutuskonferenssi. Sen yhteydessä järjestettiin myös ensimmäinen International Montessori
        Congress.
      </p>
      <p>
        Maria Montessori piti tapahtuman aikana luentoja ja Montessori-filosofiaan ja
        -pedagogiikkaan liittyvää opetusta. Maria ja Mario Montessori loivat Helsingørissä myös
        perustan Association Montessori Internationalelle, joka perustettiin vuonna 1929
        suojelemaan, kehittämään ja levittämään Montessorin työtä kansainvälisesti.
      </p>
      <p>
        Vuoden 1929 tapahtuma kesti 8.–21. elokuuta. Maria ja Mario majoittuivat
        Marienlyst-hotellissa — juuri samassa paikassa, johon vuoden 2026 pohjoismainen
        Montessori-konferenssi nyt kokoontuu.
      </p>
      <p>
        Siksi tapahtuma ei ole järjestäjille vain tavallinen koulutusviikonloppu. Se on samalla
        paluu Montessori-liikkeen historiaan. Vuonna 2029 AMI täyttää 100 vuotta, joten vuoden 2026
        konferenssi toimii myös tulevan merkkivuoden ennakkojuhlana.
      </p>

      <figure className="not-prose my-10">
        <img
          src={konferenssi}
          alt="Vaalea pohjoismainen konferenssisali, jossa osallistujat keskustelevat"
          width={1280}
          height={720}
          loading="lazy"
          className="aspect-[16/9] w-full rounded-3xl border border-border object-cover"
        />
        <figcaption className="mt-2 text-center text-xs text-muted-foreground">
          Kolmen päivän ajan Helsingør kokoaa yhteen opettajia, tutkijoita ja vanhempia neljästä
          Pohjoismaasta.
        </figcaption>
      </figure>

      <h2>Pohjoismaat saman pöydän ääreen</h2>
      <p>
        Nordic Montessori Conference järjestetään joka toinen vuosi, ja isäntämaa kiertää Norjan,
        Ruotsin, Suomen ja Tanskan välillä. Vuonna 2026 vuoro on Tanskalla.
      </p>
      <p>
        Järjestäjän arvion mukaan Helsingøriin kokoontuu noin sata osallistujaa eri puolilta
        maailmaa: Montessori-opettajia, tutkijoita, vanhempia, koulunjohtajia, terveydenhuollon
        ammattilaisia sekä muita kasvatuksesta kiinnostuneita.
      </p>
      <p>
        Perjantain avauksessa nähdään heti Montessori-maailman kansainvälisiä johtohahmoja.
        Konferenssin avaa AMI:n presidentti Dr. Alain Tschudin, minkä jälkeen puhuu AMI:n
        toiminnanjohtaja Lynne Lawrence. Päivän yhteisessä keskustelussa ovat mukana myös Norjan,
        Ruotsin, Suomen ja Tanskan kansallisten Montessori-yhdistysten edustajat.
      </p>

      <h2>Montessori ei vuonna 2026 tarkoita vain päiväkotia</h2>
      <p>
        Konferenssin ehkä kiinnostavin piirre tavallisen vanhemman näkökulmasta löytyy ohjelman
        laajuudesta. Montessori yhdistetään Suomessa edelleen helposti pieniin lapsiin, puisiin
        materiaaleihin ja päiväkoti-ikäisten itsenäiseen tekemiseen. Helsingørin ohjelma näyttää
        paljon laajemman kuvan.
      </p>
      <p>Lauantaina ohjelmassa käsitellään esimerkiksi:</p>
      <ul>
        <li>vauva- ja taaperoikäisten Montessori-pedagogiikkaa,</li>
        <li>riskissä olevien lasten tukemista,</li>
        <li>Montessori-arkkitehtuuria,</li>
        <li>liikuntaa ja urheilua,</li>
        <li>Montessori-periaatteiden soveltamista muistisairaiden hoivaan</li>
        <li>sekä uuden Montessori-koulun perustamista.</li>
      </ul>
      <p>
        Jo tämä kertoo paljon siitä, mihin suuntaan kansainvälinen Montessori-keskustelu on menossa.
        Kyse ei ole enää ainoastaan siitä, mitä materiaalia lapselle asetetaan hyllylle. Kysymys on
        enemmän siitä: millainen ympäristö auttaa ihmistä toimimaan mahdollisimman itsenäisesti ja
        tarkoituksenmukaisesti?
      </p>

      <h2>Suomalaisittain kiinnostava nimi: Tiina Suominen</h2>
      <p>
        Lauantaiaamun ohjelmassa esiintyy myös Tiina Suominen, joka toimii AMI:n 0–3-vuotiaiden
        Montessori-kouluttajana. Suomisella on AMI:n 0–3-diplomi Denveristä ja 3–6-diplomi
        Washington D.C:stä, ja hänen puheenvuoronsa keskittyy Montessori-ajatteluun aivan elämän
        ensimmäisistä vuosista alkaen.
      </p>
      <p>
        Se on kiinnostavaa juuri siksi, että yksi Montessori-ajattelun perusajatuksista alkaa paljon
        ennen koulua. Pieni lapsi ei tarvitse jatkuvaa aikuisen järjestämää viihdettä. Hän tarvitsee
        ympäristön, jossa voi turvallisesti liikkua, koskettaa, tutkia, toistaa, epäonnistua, yrittää
        uudelleen ja vähitellen tehdä enemmän itse.
      </p>

      <h2>Mitä tekemistä urheilulla on Montessorin kanssa?</h2>
      <p>
        Yksi konferenssin ehkä yllättävimmistä aiheista on Montessori Sports. Lauantaina siitä puhuu
        hankkeen toinen perustaja Patrick Oudejans, jolla on taustaa muun muassa AFC Ajaxin nuorten
        jalkapalloakatemiasta. Sunnuntaiaamuna aihe ei jää pelkäksi luennoksi: osallistujille
        järjestetään aktiivinen Montessori Sports -työpaja Helsingørin rannalla.
      </p>
      <p>
        Ajatus sopii yllättävän hyvin Montessorin alkuperäiseen filosofiaan. Liike ei ollut Maria
        Montessorille erillinen “liikuntatunti”. Kehon käyttäminen oli osa lapsen oppimista.
      </p>
      <MiksiToimii>
        Kun lapsi kantaa, tasapainoilee, kiipeää, siirtää esineitä tai harjoittelee tarkkoja
        liikkeitä, hän ei kehitä vain lihaksiaan. Hän harjoittelee samalla kehon hallintaa,
        itsesäätelyä, keskittymistä, suunnittelua ja oman toimintansa arvioimista.
      </MiksiToimii>

      <figure className="not-prose my-10">
        <img
          src={liike}
          alt="Lapsia juoksemassa pohjoismaisella hiekkarannalla loppukesän valossa"
          width={1280}
          height={720}
          loading="lazy"
          className="aspect-[16/9] w-full rounded-3xl border border-border object-cover"
        />
        <figcaption className="mt-2 text-center text-xs text-muted-foreground">
          Sunnuntain Montessori Sports -työpaja pidetään Helsingørin rannalla.
        </figcaption>
      </figure>

      <h2>Montessori ja arkkitehtuuri — miksi huoneella on väliä?</h2>
      <p>
        Steve Lawrence käsittelee konferenssissa Montessori-arkkitehtuuria ja sitä, kuinka fyysiset
        ympäristöt voivat tukea itsenäisyyttä ja luovuutta. Tämä on yksi Montessori-ajattelun
        kohdista, jonka tavallinen perhe voi ymmärtää hyvin helposti.
      </p>
      <p>
        Kuvittele kaksivuotias lapsi tavallisessa aikuisten keittiössä. Lasit ovat korkeassa
        kaapissa, vesihana on liian korkealla, pöytä on suuri ja tuoli täytyy nostaa paikalleen
        aikuisen avulla. Lähes jokainen pieni tehtävä vaatii: “Äiti, auta.”
      </p>
      <p>
        Entä jos ympäristöä muutetaan hieman? Lapsella on oma pieni vesikannu. Lasit ovat hänen
        ulottuvillaan. Pyyhe löytyy alhaalta. Tavaroilla on selkeät paikat. Yhtäkkiä sama lapsi
        pystyy tekemään osan asioista itse. Lapsi ei muuttunut — ympäristö muuttui. Juuri tämä
        ajatus on yksi Montessori-pedagogiikan keskeisistä lähtökohdista.
      </p>

      <h2>Ehkä kaikkein yllättävin aihe: Montessori ja muistisairaudet</h2>
      <p>
        Carolyn Magnussen puhuu Montessori-periaatteiden käyttämisestä dementiaa sairastavien
        ihmisten hoidossa. Magnussen on Montessori for Dementia, Disability and Ageing -ammattilainen
        ja Montessori Caren toinen perustaja. Hänen työnsä keskittyy siihen, miten ympäristö voidaan
        rakentaa tukemaan muistisairaan ihmisen itsenäisyyttä, merkityksellisyyden kokemusta ja
        hyvinvointia.
      </p>
      <p>
        Ajatus voi kuulostaa aluksi oudolta. Miten lasten pedagogiikka liittyy vanhusten hoitoon?
        Yhteys löytyy yhdestä hyvin montessorilaisesta kysymyksestä: mitä ihminen pystyy vielä
        tekemään itse, jos ympäristö auttaa häntä onnistumaan?
      </p>
      <p>
        Montessori-ajattelun näkökulmasta itsenäisyys ei siis ole vain lapsuuden tavoite. Se voi olla
        arvokasta koko elämän ajan. Tämä selittää myös sen, miksi vuoden 2026 konferenssi kuvailee
        Montessori-lähestymistapaa jatkumona syntymästä vanhuuteen.
      </p>

      <h2>Sunnuntaina puhutaan nykyajan haasteista</h2>
      <p>
        Konferenssi ei kuitenkaan katso vain historiaan. Sunnuntain ohjelmassa AMI 6–12 -opettaja ja
        entinen ohjelmistoinsinööri Elena Espas vetää työpajan nykyajan Montessori-ympäristön
        haasteista.
      </p>
      <p>
        Aihe on erityisen ajankohtainen vuonna 2026. Lasten maailma sisältää nyt asioita, joita Maria
        Montessori ei olisi voinut kuvitella: älypuhelimet, algoritmit, sosiaalisen median, tekoälyn,
        pelit ja jatkuvan digitaalisen ärsyketulvan.
      </p>
      <p>
        Siksi yksi Montessori-liikkeen suurimmista tulevaisuuden kysymyksistä ei ole se, kuinka
        tarkasti yli sata vuotta vanhaa luokkahuonetta pitäisi kopioida. Paljon kiinnostavampi
        kysymys on: miten Montessorin ajatuksia keskittymisestä, itsenäisyydestä ja todellisesta
        tekemisestä voidaan soveltaa vuonna 2026 syntyvän lapsen maailmaan?
      </p>

      <h2>Norjan Montessori-tarina tarjoaa yhden kiinnostavan esimerkin</h2>
      <p>
        Konferenssin viimeisiä puheenvuoroja pitää Montessori Norgen johtaja Nina Johansen aiheella
        A Path to Recognition. Montessori Norge kertoo, että Nordic Montessori Conference
        järjestetään joka toinen vuosi ja vastuu tapahtumasta kiertää neljän Pohjoismaan kesken.
      </p>
      <p>
        Pohjoismaiden välillä voidaan siksi jakaa myös kokemuksia siitä, miten Montessori toimii
        osana hyvin erilaisia kansallisia koulujärjestelmiä. Suomen kannalta tämä on arvokasta.
        Meidän ei tarvitse hakea kaikkia esimerkkejä Yhdysvalloista tai Keski-Euroopasta. Paljon
        voidaan oppia myös naapurimaista, joissa lasten arki, yhteiskunta ja koulujärjestelmät ovat
        lähempänä omaamme.
      </p>

      <h2>Miksi vuoden 2026 konferenssi on tavallista kiinnostavampi?</h2>
      <p>
        Jos ohjelmaa katsoo kokonaisuutena, Helsingørin konferenssissa kohtaavat kolme aikakautta.
      </p>
      <ul>
        <li>
          <strong>Menneisyys:</strong> Maria ja Mario Montessori kävelivät samoissa maisemissa
          vuonna 1929.
        </li>
        <li>
          <strong>Nykyisyys:</strong> opettajat ja asiantuntijat keskustelevat tämän päivän
          kouluista, lapsista, liikkumisesta, erityistarpeista ja ympäristöistä.
        </li>
        <li>
          <strong>Tulevaisuus:</strong> Montessori-liikkeen täytyy löytää oma vastauksensa
          teknologian, muuttuvan koulun ja uudenlaisten lapsuuden ympäristöjen keskellä.
        </li>
      </ul>
      <p>
        Ehkä juuri tämän vuoksi nimi Where It All Began sopii tapahtumaan niin hyvin. Ei siksi, että
        Montessori olisi palaamassa takaisin vuoteen 1929, vaan siksi, että lähes sata vuotta
        myöhemmin voidaan palata samaan paikkaan ja kysyä uudelleen: mitä lapsi todella tarvitsee
        kasvaakseen itsenäiseksi ihmiseksi?
      </p>
      <p>
        Teknologia on muuttunut. Kodit ovat muuttuneet. Koulut ovat muuttuneet. Yhteiskunta on
        muuttunut. Mutta lapsi oppii edelleen samalla perustavalla tavalla: liikkumalla,
        koskettamalla, tutkimalla, keskittymällä, epäonnistumalla ja yrittämällä uudelleen. Ja juuri
        sitä keskustelua jatketaan Helsingørissä elokuussa 2026.
      </p>

      <h2>Nordic Montessori Conference 2026</h2>
      <ul>
        <li>
          <strong>Milloin:</strong> 28.–30. elokuuta 2026
        </li>
        <li>
          <strong>Missä:</strong> Marienlyst Strandhotel, Helsingør, Tanska
        </li>
        <li>
          <strong>Tapahtuma:</strong> 9th Nordic Montessori Conference
        </li>
        <li>
          <strong>Teema:</strong> Where It All Began – A Pre-Celebration
        </li>
        <li>
          <strong>Konferenssikieli:</strong> englanti
        </li>
      </ul>

      <p>
        Jos haluat lukea lisää Montessorin taustasta, tutustu{" "}
        <Link to="/montessori-koulu/maria-montessori-elamakerta">Maria Montessorin elämäkertaan</Link>{" "}
        tai katsaukseen{" "}
        <Link to="/montessori-koulu/montessori-suomessa-2026">Montessori Suomessa 2026</Link>.
      </p>
    </BlogPost>
  );
}
